import React from 'react';
import PropTypes from 'prop-types';
import { CircularProgress } from '@material-ui/core';
import { compose } from 'redux';
import { connect } from 'react-redux';
import AdminContainer from '../../common/AdminContainer';
import VehicleForm from '../VehicleForm';
import {
    getVehicleDetails,
    isUserDetailLoading,
} from '../../stores/users/selectors';
import { createVehicle, updateVehicle } from '../../stores/users/actions';
import { FETCH_USER_ACTIONS } from '../../stores/users/apiActions';
import {
    removeFormMessage,
    updateVehiclePhotosRequested,
    getVehicleMakesRequested,
    getVehicleModelsRequested,
} from '../../stores/vehicles/actions';
import {
    areVehicleUpdatesInProgress,
    getMessages,
    getVehicleMakes,
    getVehicleModels,
} from '../../stores/vehicles/selectors';
import { formMessagesPropTypes } from '../../common/FormMessages';
import NewVehicleForm from '../VehicleForm/NewVehicle';

class VehicleDetailsContainer extends React.Component {
    componentDidMount() {
        window.scrollTo(0, 0);
        const { vehicle, userId, vehicleId, fetchUser } = this.props;
        if (!vehicle || vehicleId !== vehicle.vehicleId) {
            fetchUser(userId);
        }
        this.props.requestVehicleMakes();
        this.props.requestVehicleModels();
    }

    renderForm = () => {
        const {
            vehicle = {},
            loading,
            onSubmit,
            vehicleId,
            messages,
            removeFormMessage,
            onNewSubmit,
            vehicleMakes,
            vehicleModels,
            photoUpdate,
        } = this.props;

        if (loading) {
            return <CircularProgress />;
        }
        if (!vehicle.vehicleId) {
            return (
                <NewVehicleForm
                    formState={{}}
                    onSubmit={(data, photos) => {
                        onNewSubmit(data, this.props.userId, photos);
                    }}
                    loading={loading}
                    messages={messages}
                    removeFormMessage={removeFormMessage}
                    makes={vehicleMakes}
                    models={vehicleModels}
                    userId={this.props.userId}
                />
            );
        }

        return (
            <VehicleForm
                formState={{ ...vehicle }}
                onSubmit={(data, photos) => {
                    photoUpdate(data.vehicleId, photos);
                    onSubmit(data, this.props.userId);
                }}
                loading={loading}
                vehicleId={vehicleId}
                messages={messages}
                removeFormMessage={removeFormMessage}
            />
        );
    };

    render() {
        const { path, userId, ...rest } = this.props;

        return (
            <AdminContainer
                path={path}
                innerHeader
                innerHeaderProps={{
                    title: 'Vehicle',
                    backText: '< Back to User',
                    backTo: `/users/${userId}`,
                }}
                {...rest}
            >
                {this.renderForm()}
            </AdminContainer>
        );
    }
}

VehicleDetailsContainer.propTypes = {
    // The path which should be passed down from the router.
    path: PropTypes.string.isRequired,

    // The ID of the vehicle in the view, a route parameter.
    vehicleId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

    // The ID of the user who owns the vehicle being viewed, a route parameter.
    userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

    // Indication of whether or not the vehicle data is loading.
    loading: PropTypes.bool,

    // The vehicle in the view
    vehicle: PropTypes.object,

    // Messages to be displayed on the form, if any.
    messages: formMessagesPropTypes.messages,

    removeFormMessage: PropTypes.func.isRequired,

    // Fetch user function
    fetchUser: PropTypes.func.isRequired,

    // Submit function
    onSubmit: PropTypes.func.isRequired,

    // New submit function
    onNewSubmit: PropTypes.func.isRequired,

    // Functions to get vehicle makes and models
    requestVehicleMakes: PropTypes.func.isRequired,
    requestVehicleModels: PropTypes.func.isRequired,

    // Vehicle makes and models
    vehicleMakes: PropTypes.arrayOf(
        PropTypes.shape({
            makeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            makeName: PropTypes.string,
        }),
    ),
    vehicleModels: PropTypes.arrayOf(
        PropTypes.shape({
            modelId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            modelName: PropTypes.string,
            makeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        }),
    ),

    photoUpdate: PropTypes.func.isRequired,
};

const mapStateToProps = function(state) {
    return {
        loading:
            isUserDetailLoading(state) || areVehicleUpdatesInProgress(state),
        vehicle: getVehicleDetails(state),
        messages: getMessages(state),
        vehicleModels: getVehicleModels(state),
        vehicleMakes: getVehicleMakes(state),
    };
};

const mapDispatchToProps = function(dispatch) {
    return {
        fetchUser: (userId) => dispatch(FETCH_USER_ACTIONS.requested(userId)),
        photoUpdate: (vehicleId, photos) =>
            dispatch(updateVehiclePhotosRequested(vehicleId, photos)),
        onSubmit: (vehicle, userId) => dispatch(updateVehicle(vehicle, userId)),
        onNewSubmit: (vehicle, userId, photos) =>
            dispatch(createVehicle(vehicle, userId, photos)),
        removeFormMessage: (messageKey) =>
            dispatch(removeFormMessage(messageKey)),
        requestVehicleModels: () => dispatch(getVehicleModelsRequested()),
        requestVehicleMakes: () => dispatch(getVehicleMakesRequested()),
    };
};

export default compose(connect(mapStateToProps, mapDispatchToProps))(
    VehicleDetailsContainer,
);
